/**
 * Central brand / theming source of truth.
 *
 * The primary colour comes from the environment so that the same codebase can be
 * deployed under different brands (xNotify, STC, ...) without a code change.
 *
 *   .env  ->  REACT_APP_PRIMARY_COLOR=#4f008c
 *
 * IMPORTANT (Create React App): env vars MUST be prefixed `REACT_APP_` or CRA
 * strips them, and they are inlined at BUILD time — not read at runtime. Each
 * deployment therefore has to run its own `npm run build` with its own .env.
 *
 * Everything else (hover, active, tints, borders, focus rings, the Chakra 50-900
 * ramp) is DERIVED from that single colour, so one env var retints the whole app
 * coherently instead of leaving mismatched blues behind.
 *
 * Two ways to consume it:
 *
 *   1. CSS variables — preferred, and what the bulk of the app uses. Works in
 *      Chakra style props, inline styles, plain CSS and template strings:
 *          <Button bg="var(--brand-primary)" _hover={{ bg: 'var(--brand-primary-hover)' }} />
 *          box-shadow: 0 0 0 2px rgb(var(--brand-primary-rgb) / 0.15);
 *
 *   2. The JS exports below — required wherever CSS variables cannot reach:
 *      Highcharts / Chart.js (canvas), @react-pdf/renderer, and any string that
 *      leaves the browser (e.g. exported email HTML).
 */

/** Fallback used when REACT_APP_PRIMARY_COLOR is absent or malformed. */
// xNotify blue. With no REACT_APP_PRIMARY_COLOR set the app must look exactly
// like it did before branding was made configurable, so this is the ORIGINAL
// colour, not the STC purple. STC sets the env var explicitly.
const DEFAULT_PRIMARY = "#2563eb";

/* ------------------------------------------------------------------ *
 * colour maths
 * ------------------------------------------------------------------ */

/** Accepts `#abc` / `#aabbcc`, returns `{ r, g, b }` or null. */
function parseHex(input) {
  const hex = String(input || '').trim().replace(/^#/, '');
  const full = hex.length === 3 ? hex.replace(/./g, (c) => c + c) : hex;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

function rgbToHsl({ r, g, b }) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;
  if (delta !== 0) {
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    if (max === rn) h = ((gn - bn) / delta) % 6;
    else if (max === gn) h = (bn - rn) / delta + 2;
    else h = (rn - gn) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: s * 100, l: l * 100 };
}

function hslToHex({ h, s, l }) {
  const sn = clamp(s, 0, 100) / 100;
  const ln = clamp(l, 0, 100) / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));

  let rgb;
  if (hp < 1) rgb = [c, x, 0];
  else if (hp < 2) rgb = [x, c, 0];
  else if (hp < 3) rgb = [0, c, x];
  else if (hp < 4) rgb = [0, x, c];
  else if (hp < 5) rgb = [x, 0, c];
  else rgb = [c, 0, x];

  const m = ln - c / 2;
  return (
    '#' +
    rgb
      .map((v) => Math.round(clamp((v + m) * 255, 0, 255)).toString(16).padStart(2, '0'))
      .join('')
  );
}

/** Same hue/saturation as `hex`, forced to lightness `l` (0-100). */
function atLightness(hex, l) {
  const hsl = rgbToHsl(parseHex(hex));
  return hslToHex({ h: hsl.h, s: hsl.s, l });
}

/**
 * Lighter variant: moves `ratio` of the way from the base lightness toward 97.
 * Relative rather than absolute so the result is always lighter than the base,
 * whatever lightness the brand colour happens to have.
 */
function lighten(hex, ratio) {
  const { l } = rgbToHsl(parseHex(hex));
  return atLightness(hex, l + (97 - l) * ratio);
}

/**
 * Darker variant: scales the base lightness by `factor`. Multiplicative (not a
 * fixed subtraction) so a dark brand colour like #4f008c still produces a
 * monotonic ramp instead of collapsing into itself.
 */
function darken(hex, factor) {
  const { l } = rgbToHsl(parseHex(hex));
  return atLightness(hex, l * factor);
}

/* ------------------------------------------------------------------ *
 * the brand palette
 * ------------------------------------------------------------------ */

/**
 * Hand-tuned ramps for brands we ship, keyed by primary colour.
 *
 * The generated ramp below is a good approximation, but Tailwind's blue family
 * (which the app was hardcoded against) shifts hue and saturation per step, so a
 * pure hue-preserving derivation lands a few points off. Pinning the exact
 * values for the default blue keeps the xNotify build byte-identical to the
 * pre-refactor colours, so QA only has to eyeball the *new* brand.
 *
 * Add an entry here when a brand needs exact control; otherwise the generated
 * ramp is used and a single env var is enough.
 */
const KNOWN_RAMPS = {
  '#2563eb': {
    hover: '#1d4ed8',
    active: '#1e40af',
    tint: '#eff6ff',
    tintStrong: '#dbeafe',
    border: '#bfdbfe',
    scale: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#2563eb',
      600: '#1d4ed8',
      700: '#1e40af',
      800: '#1e3a8a',
      900: '#172554',
    },
  },
};

/**
 * Read straight off `process.env` so CRA can statically inline it — do not
 * refactor into a dynamic lookup, that silently breaks the substitution.
 */
export const PRIMARY_BG = parseHex(process.env.REACT_APP_PRIMARY_COLOR)
  ? String(process.env.REACT_APP_PRIMARY_COLOR).trim().toLowerCase()
  : DEFAULT_PRIMARY;

/**
 * True only when REACT_APP_PRIMARY_COLOR is actually set to a usable colour.
 *
 * Exposed for any surface that needs to behave differently on a branded vs the
 * default build. Nothing consumes it today — the login screen, the only such
 * surface, is unconditionally pinned to the original blue in login.css.
 */
export const IS_BRANDED = Boolean(parseHex(process.env.REACT_APP_PRIMARY_COLOR));

const tuned = KNOWN_RAMPS[PRIMARY_BG] || {};

/** Reads an optional exact-value override from the environment. */
function envColor(value) {
  return parseHex(value) ? String(value).trim().toLowerCase() : null;
}

/** Interactive states. Env override wins, then hand-tuned ramp, then derived. */
export const PRIMARY_BG_HOVER =
  envColor(process.env.REACT_APP_PRIMARY_COLOR_HOVER) || tuned.hover || darken(PRIMARY_BG, 0.9);

export const PRIMARY_BG_ACTIVE =
  envColor(process.env.REACT_APP_PRIMARY_COLOR_ACTIVE) || tuned.active || darken(PRIMARY_BG, 0.75);

/** Very light wash used for selected rows, badges, callout backgrounds. */
export const PRIMARY_TINT = tuned.tint || lighten(PRIMARY_BG, 1);

/** A step up from TINT — hovered tint surfaces, chart fills. */
export const PRIMARY_TINT_STRONG = tuned.tintStrong || lighten(PRIMARY_BG, 0.9);

/** Borders on tinted surfaces. */
export const PRIMARY_BORDER = tuned.border || lighten(PRIMARY_BG, 0.72);

/** Space-separated channels, for `rgb(var(--...-rgb) / 0.3)` alpha usage. */
function channels(hex) {
  const { r, g, b } = parseHex(hex);
  return `${r} ${g} ${b}`;
}

/** `"37 99 235"` — for `rgb(var(--brand-primary-rgb) / 0.3)` alpha usage. */
export const PRIMARY_RGB = channels(PRIMARY_BG);

/** Channels of the tint, for translucent tinted surfaces. */
export const TINT_RGB = channels(PRIMARY_TINT);

/** Build an rgba() string from the brand colour at an arbitrary alpha. */
export function primaryAlpha(alpha) {
  const { r, g, b } = parseHex(PRIMARY_BG);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Chakra-shaped 50-900 ramp. Hand-tuned when known, otherwise generated. */
export const PRIMARY_SCALE = tuned.scale || {
  50: lighten(PRIMARY_BG, 1),
  100: lighten(PRIMARY_BG, 0.93),
  200: lighten(PRIMARY_BG, 0.82),
  300: lighten(PRIMARY_BG, 0.65),
  400: lighten(PRIMARY_BG, 0.38),
  500: PRIMARY_BG,
  600: darken(PRIMARY_BG, 0.8),
  700: darken(PRIMARY_BG, 0.62),
  800: darken(PRIMARY_BG, 0.46),
  900: darken(PRIMARY_BG, 0.32),
};

/**
 * Logos live in public/ (NOT src/), so each deployment can swap the file without
 * a code change and without webpack fingerprinting the filename:
 *   public/Logo.png       -> xNotify
 *   public/STClogo.png    -> STC
 *
 * Set a `/`-rooted path or an absolute URL:
 *   REACT_APP_LOGO_URL=/STClogo.png
 *   REACT_APP_LOGO_URL=https://cdn.example/logo.png
 */
export const LOGO_URL = (process.env.REACT_APP_LOGO_URL || '').trim();

/** Public path used when REACT_APP_LOGO_URL is not set. */
const DEFAULT_LOGO = '/Logo.png';

/**
 * Prefixes `/`-rooted paths with PUBLIC_URL so a public/ asset still resolves when
 * the app is served from a sub-path. Absolute and data URLs are passed through.
 *
 * `varName` only feeds the dev warning — it names the var that actually needs
 * correcting, since several of them (logo, favicon, touch icon) come through here.
 */
function resolveAsset(pathOrUrl, varName = 'REACT_APP_LOGO_URL') {
  const raw = String(pathOrUrl || '').trim();
  if (!raw) return '';
  if (/^(https?:|data:|blob:)/i.test(raw)) return raw;

  // `public/` is the web ROOT at runtime, not a URL segment, so "public/Logo.png"
  // would 404. That is the single easiest mistake to make when editing .env per
  // deployment, so normalise it instead of silently serving a broken image.
  // Accepts: /Logo.png  |  Logo.png  |  public/Logo.png  |  ./public/Logo.png
  let normalised = raw.replace(/^\.?\/?public\//i, '/');
  if (!normalised.startsWith('/')) normalised = '/' + normalised;

  if (normalised !== raw && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      `[brand] ${varName}="${raw}" was normalised to "${normalised}". ` +
        'Paths are relative to the web root: drop the "public/" prefix and start with "/".'
    );
  }

  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return base + normalised;
}

/**
 * The logo every screen should render. Import this rather than referencing a
 * file directly:
 *   import { LOGO_SRC } from '.../theme/brand';
 *   <Image src={LOGO_SRC} />
 */
export const LOGO_SRC = resolveAsset(LOGO_URL || DEFAULT_LOGO);

/**
 * Browser-tab icon. Lives in public/ alongside the logo, same rules:
 *   REACT_APP_FAVICON_URL=/stcfavicon.ico
 *
 * Applied by src/index.js, which rewrites the <link rel="icon"> that
 * public/index.html ships. index.html cannot reference this itself — CRA leaves
 * `%REACT_APP_FAVICON_URL%` in place verbatim when the var is unset, and .env is
 * gitignored, so a fresh clone would request a nonexistent file and show no icon.
 */
export const FAVICON_SRC = resolveAsset(
  (process.env.REACT_APP_FAVICON_URL || '').trim() || '/favicon.ico',
  'REACT_APP_FAVICON_URL'
);

/**
 * iOS home-screen icon. Must be a PNG (an .ico is ignored by Safari), which is why
 * it is a separate var from the favicon rather than reusing it.
 */
export const APPLE_TOUCH_ICON_SRC = resolveAsset(
  (process.env.REACT_APP_APPLE_TOUCH_ICON_URL || '').trim() || '/logo192.png',
  'REACT_APP_APPLE_TOUCH_ICON_URL'
);

/**
 * Documentation screenshots. These are captures of the app's OWN UI, so they show
 * the brand colour, logo and product name — a rebranded deployment needs its own.
 *
 * Drop the replacement in public/ and point the var at it:
 *   REACT_APP_WEBHOOK_DOC_IMAGE_URL=/stc-webhook-doc.png
 *
 * Unset resolves to '' (resolveAsset returns '' for an empty path), and each page
 * falls back to `SRC || <its own import>`. That keeps the default screenshot bundled
 * and content-hashed by webpack exactly as before — only an overridden one is served
 * from public/, where it is not fingerprinted and may need a cache-busting rename.
 *
 * Both webhook doc routes are live and show different captures:
 *   /documentation/webhook-doc  → WEBHOOK_DOC_IMAGE_SRC, WEBHOOK_POPUP_IMAGE_SRC
 *   /web-hook (legacy)          → WEBHOOK_CONFIG_IMAGE_SRC, WEBHOOK_LEGACY_IMAGE_SRC
 */
export const WEBHOOK_DOC_IMAGE_SRC = resolveAsset(
  (process.env.REACT_APP_WEBHOOK_DOC_IMAGE_URL || '').trim(),
  'REACT_APP_WEBHOOK_DOC_IMAGE_URL'
);
export const WEBHOOK_POPUP_IMAGE_SRC = resolveAsset(
  (process.env.REACT_APP_WEBHOOK_POPUP_IMAGE_URL || '').trim(),
  'REACT_APP_WEBHOOK_POPUP_IMAGE_URL'
);
export const WEBHOOK_CONFIG_IMAGE_SRC = resolveAsset(
  (process.env.REACT_APP_WEBHOOK_CONFIG_IMAGE_URL || '').trim(),
  'REACT_APP_WEBHOOK_CONFIG_IMAGE_URL'
);
export const WEBHOOK_LEGACY_IMAGE_SRC = resolveAsset(
  (process.env.REACT_APP_WEBHOOK_LEGACY_IMAGE_URL || '').trim(),
  'REACT_APP_WEBHOOK_LEGACY_IMAGE_URL'
);

/**
 * Reads a text env var, falling back when it is absent OR blank.
 *
 * Trim BEFORE the fallback, not after. `(value || fallback).trim()` looks equivalent
 * but is not: a var present-but-whitespace (`REACT_APP_BRAND_NAME=` followed by
 * spaces, or a value whose inline comment left only blanks behind) is a truthy
 * string, so it wins the `||` and then trims down to `''` — blanking the product
 * name in every heading, placeholder and report footer with no error anywhere.
 */
function envText(value, fallback) {
  const text = String(value == null ? '' : value).trim();
  return text || fallback;
}

/**
 * Company / product name for every piece of DISPLAYED copy — headings, body text,
 * placeholders, report footers, the docs pages.
 *
 *   .env  ->  REACT_APP_BRAND_NAME=STC
 *
 * Import it rather than typing the name:
 *   import { BRAND_NAME } from '.../theme/brand';
 *   <Text>Sign in to your {BRAND_NAME} account</Text>
 *   `Integrate the ${BRAND_NAME} SDK…`
 *
 * What this deliberately does NOT cover, because renaming these breaks things
 * rather than rebranding them:
 *   • hostnames and API bases (api.xnotify.ai, story.xnotify.com) — those are
 *     REACT_APP_*_URL vars, or real infrastructure;
 *   • localStorage keys and DOM event names (`xnotify-ai-suggestions`) — renaming
 *     silently discards existing users' stored state;
 *   • payload/response keys (`event.xnotify_properties`) — set by the backend;
 *   • published SDK identifiers in copy-paste code samples (package names, the
 *     `com.example.xnotifysdktest` sample package) — they must match what the
 *     customer actually installs;
 *   • the `.logo-xnotify` CSS class and `public/xnotify_csv_guide.pdf` filename.
 *
 * public/index.html hardcodes the DEFAULT below, and src/index.js overwrites the
 * title/description from this value on boot, so an env-less build still reads
 * correctly. public/manifest.json is NOT substituted by CRA, so its name/short_name
 * still need a manual edit per deployment.
 */
export const BRAND_NAME = envText(process.env.REACT_APP_BRAND_NAME, 'xNotify');

/** Address behind "Contact Support" links. */
export const SUPPORT_EMAIL = envText(process.env.REACT_APP_SUPPORT_EMAIL, 'support@xnotify.com');

/* ------------------------------------------------------------------ *
 * CSS custom properties
 * ------------------------------------------------------------------ */

/**
 * Publishes the palette as CSS variables on <html>. Called on import (below) so
 * the variables exist before React mounts and nothing paints unbranded.
 */
export function applyBrandCssVars(target) {
  const root = target || (typeof document !== 'undefined' && document.documentElement);
  if (!root) return;
  const vars = {
    '--brand-primary': PRIMARY_BG,
    '--brand-primary-hover': PRIMARY_BG_HOVER,
    '--brand-primary-active': PRIMARY_BG_ACTIVE,
    '--brand-primary-rgb': PRIMARY_RGB,
    '--brand-tint-rgb': TINT_RGB,
    '--brand-tint': PRIMARY_TINT,
    '--brand-tint-strong': PRIMARY_TINT_STRONG,
    '--brand-border': PRIMARY_BORDER,
  };
  // Expose the full 50-900 ramp too (--brand-50 ... --brand-900). The indigo and
  // cyan accent systems in the story editor / Asklytics / XFlow use specific
  // shades (indigo-400, indigo-100, ...), so they need per-step targets rather
  // than collapsing everything onto --brand-primary.
  Object.entries(PRIMARY_SCALE).forEach(([step, value]) => {
    vars['--brand-' + step] = value;
    // "r g b" triple as well, so Tailwind can express these as
    // rgb(var(--brand-500-rgb) / <alpha-value>) and keep opacity modifiers
    // (bg-indigo-600/10, ring-indigo-400/40) working. A bare hex in a var()
    // silently drops the alpha.
    vars['--brand-' + step + '-rgb'] = channels(value);
  });
  Object.entries(vars).forEach(([key, value]) => root.style.setProperty(key, value));
}

applyBrandCssVars();

const brand = {
  PRIMARY_BG,
  PRIMARY_BG_HOVER,
  PRIMARY_BG_ACTIVE,
  PRIMARY_TINT,
  PRIMARY_TINT_STRONG,
  PRIMARY_BORDER,
  PRIMARY_RGB,
  PRIMARY_SCALE,
  LOGO_URL,
  LOGO_SRC,
  FAVICON_SRC,
  APPLE_TOUCH_ICON_SRC,
  BRAND_NAME,
  SUPPORT_EMAIL,
  primaryAlpha,
};

export default brand;
